import { Box, Grid, useMediaQuery } from "@mui/material";
import {
  ArrayField,
  BooleanField,
  Datagrid,
  DateField,
  FunctionField,
  Labeled,
  NumberField,
  ReferenceField,
  TextField,
  useRecordContext,
} from "react-admin";

import { settings } from "app";
import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { employeeRequests } from "api/employeeApi";
import { SectionTitle } from "features/SectionTitle";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import SecuredComponent from "server/security/SecuredComponent";
import EditInternshipsDialog from "./components/EditInternshipsDialog";

const renderLocations = internship =>
  internship?.locations
    ?.map(item => item?.location)
    .filter(Boolean)
    .join(", ") || "-";

// The long free-text fields and the locations would make the row unreadable, so they expand.
const InternshipExpand = () => (
  <Box sx={{ padding: 1 }}>
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Локации">
          <FunctionField render={renderLocations} />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Уведоми ръководител на катедра">
          <BooleanField source="notifyHeadOfDepartment" />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Коментар при пристигане">
          <TextField
            source="arrivalComment"
            emptyText="-"
          />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Причина за отказ">
          <TextField
            source="rejectionReason"
            emptyText="-"
          />
        </Labeled>
      </Grid>
    </Grid>
  </Box>
);

const InternshipsSection = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const record = useRecordContext();

  if (!record) return null;

  return (
    <>
      <SectionTitle
        paddingY={2}
        title="Стажове"
        textAlign="center"
      />
      <Grid
        container
        spacing={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <ArrayField source="internships">
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              size="small"
              expand={<InternshipExpand />}
              sx={[
                settings.style.detailsDatagrid,
                settings.style.firstExpandIconDatagrid,
                // firstExpandIconDatagrid paints the "expand all" icon white; on the light nested header it would vanish.
                settings.style.modernUi && { "& .RaDatagrid-headerCell:first-of-type .RaDatagrid-expandIcon": { color: "inherit" } },
              ]}
            >
              <TextField
                source="name"
                label="Име на стаж"
                sortable={false}
                emptyText="-"
              />
              <DateField
                source="startDate"
                label="Начало"
                locales="bg-BG"
                sortable={false}
                emptyText="-"
              />
              <DateField
                source="endDate"
                label="Край"
                locales="bg-BG"
                sortable={false}
                emptyText="-"
              />
              <FunctionField
                label="Вид"
                sortable={false}
                render={internship => (internship?.isVoluntary ? "Доброволен" : "Задължителен")}
              />
              <NumberField
                source="credits"
                label="Кредити"
                textAlign="left"
                sortable={false}
                emptyText="-"
              />
              <ReferenceField
                source="hospitalBuildingId"
                reference={administrationRequests.getBuildingsSelect()}
                label="Болница"
                sortable={false}
                emptyText="-"
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <FunctionField
                label="Ментор"
                sortable={false}
                render={internship =>
                  internship?.employeeId ? (
                    <ReferenceField
                      source="employeeId"
                      reference={employeeRequests.getEmployeesSelect()}
                      emptyText="-"
                      link={false}
                    >
                      <FunctionField render={getProfessorRecordText} />
                    </ReferenceField>
                  ) : (
                    internship?.externalMentorName || "-"
                  )
                }
              />
            </Datagrid>
          </ArrayField>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <SecuredComponent permission={permissions.canManageErasmusInternshipStudents}>
            <EditInternshipsDialog id={record.id} />
          </SecuredComponent>
        </Grid>
      </Grid>
    </>
  );
};

export default InternshipsSection;
